import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { CurrentBrand, OrganizationData } from 'modules/Types';
import { TeamSearchResult } from 'api/brandApi';
import useOrganizationV2 from './useOrganizationV2';
import useBrand from './useBrand';
import useUser from './useUser';
import { useHistory } from 'react-router-dom';

export type AppView = 'team' | 'brand' | 'admin-team';

/** Interface for storing admin's original state before entering team view */
export interface AdminOriginalUserState {
    /** Admin's original loaded brands */
    brands: CurrentBrand[];
    /** Admin's currently selected brand */
    currentBrand: CurrentBrand | undefined;
    /** Admin's organization data */
    organization: OrganizationData | undefined;
}

/** Interface for admin team view data */
export interface AdminTeamViewData {
    /** ID of the organization being viewed */
    organizationId: number;
    /** Name of the organization being viewed */
    organizationName: string;
    /** Admin's original state to restore when exiting team view */
    originalUserState: AdminOriginalUserState;
}

interface AppViewState {
    /** Current view mode of the application */
    appView?: AppView;

    /** Admin team view state with proper typing */
    adminTeamData: AdminTeamViewData | null;

    /** Actions */
    setAppView: (view: AppView, history?: ReturnType<typeof useHistory>) => void;
    initializeAppView: () => void;
    enterAdminTeamView: (teamData: TeamSearchResult) => void;
    exitAdminTeamView: () => void;

    /** Computed getters */
    isTeamView: () => boolean;
    isBrandView: () => boolean;
    isAdminTeamView: () => boolean;

    /** Navigation helpers */
    clearBrandOnTeamViewNavigation: () => void;
}

const useAppView = create(immer<AppViewState>((set, get) => ({
    appView: undefined, // Default to brand view for backward compatibility
    adminTeamData: null,

    setAppView: (view: AppView, history?: ReturnType<typeof useHistory>) => {
        const { hasTeamViewAccess } = useOrganizationV2.getState();
        
        // Prevent members from switching to team view
        if ((view === 'team' || view === 'admin-team') && !hasTeamViewAccess()) {
            console.warn('🚫 Access denied: User does not have permission to access team view');
            return;
        }
        
        set(state => {
            state.appView = view;
        });
        console.log('🔄 App view changed to:', view);

        // Navigate to appropriate route based on app view
        if (history) {
            if (view === 'brand') {
                console.log('🏠 Navigating to Maestro page (/)');
                history.push('/');
            } else if (view === 'team') {
                console.log('🏢 Navigating to team dashboard (/team/dashboard)');
                history.push('/team/dashboard');
            } else if (view === 'admin-team') {
                console.log('🔧 Navigating to admin team dashboard (/team/dashboard)');
                history.push('/team/dashboard');
            }
        }
    },

    /**
     * Initialize app view based on current state
     * Should be called when app loads or user state changes
     */
    initializeAppView: () => {
        const { organization, hasTeamViewAccess } = useOrganizationV2.getState();
        const { currentBrand } = useBrand.getState();

        // If user has an organization but no current brand, default to team view
        // If user has no organization, default to brand view
        // If user has both organization and brand, stay in current view (don't override user's choice)

        if (organization && hasTeamViewAccess()) {
            // Auto-switch to team view if user has org but no brand and is currently in brand view
            set(state => {
                state.appView = 'team';
            });
            console.log('🔄 Auto-initialized to team view (has org, no brand)');
        } else {
            // Auto-switch to brand view if user has no org but is in team view
            set(state => {
                state.appView = 'brand';
            });
            console.log('🔄 Auto-initialized to brand view (no org)');
        }
    },

    /**
     * Explicit check for team view
     * Returns true when:
     * 1. User has an organization
     * 2. App view is explicitly set to 'team'
     * 3. User has permission to access team view (not a member)
     * This allows having a currentBrand while staying in team view
     */
    isTeamView: () => {
        const { appView } = get();
        const { hasTeamViewAccess } = useOrganizationV2.getState();
        
        // Check if app view is team/admin-team AND user has team access
        return !!(appView === 'team' || appView === 'admin-team') && hasTeamViewAccess();
    },

    /**
     * Deterministic check for brand view
     * Returns true when NOT in team view (default state)
     */
    isBrandView: () => {
        const { appView } = get();
        return appView === 'brand'
    },

    /**
     * Check if currently in admin team view
     */
    isAdminTeamView: () => {
        const { appView } = get();
        return appView === 'admin-team';
    },

    /**
     * Enter admin team view mode
     * Saves current user state and loads the selected team's data
     */
    enterAdminTeamView: (teamData: TeamSearchResult) => {
        const { loadedBrands, currentBrand } = useBrand.getState();
        const { organization } = useOrganizationV2.getState();
        const { isSymphonyAdmin } = useUser.getState();

        if (!isSymphonyAdmin) {
            return;
        }

        // Save current user state with proper typing
        const originalUserState: AdminOriginalUserState = {
            brands: loadedBrands || [],
            currentBrand,
            organization
        };

        const adminTeamData: AdminTeamViewData = {
            organizationId: teamData.organizationId,
            organizationName: teamData.organizationName,
            originalUserState
        };

        set(state => {
            state.appView = 'admin-team';
            state.adminTeamData = adminTeamData;
        });

        console.log('🔧 Entered admin team view for:', teamData.organizationName);
    },

    /**
     * Exit admin team view and restore original user state
     */
    exitAdminTeamView: () => {
        const { adminTeamData } = get();
        const { setCurrentBrand, setLoadedBrands } = useBrand.getState();
        const { setOrganization, resetOrganization } = useOrganizationV2.getState();

        if (adminTeamData?.originalUserState) {
            // Restore original state
            setLoadedBrands(adminTeamData.originalUserState.brands);
            if (adminTeamData.originalUserState.currentBrand) {
                setCurrentBrand(adminTeamData.originalUserState.currentBrand);
            }
            console.log('HOOK: exitAdminTeamView.originalUserState.organization', adminTeamData.originalUserState.organization)
            if (adminTeamData.originalUserState.organization) {
                setOrganization(adminTeamData.originalUserState.organization);
            } else {
                resetOrganization();
            }
        }

        set(state => {
            state.appView = 'brand'; // Return to default brand view
            state.adminTeamData = null;
        });

        console.log('🔧 Exited admin team view, restored original state');
    },

    /**
     * Clears currentBrand if user is in team view
     * This is useful when users navigate to team pages after exiting creation flows
     */
    clearBrandOnTeamViewNavigation: () => {
        const { isTeamView } = get();

        if (isTeamView()) {
            const { currentBrand, setCurrentBrand } = useBrand.getState();

            if (currentBrand) {
                console.log('🔄 Clearing current brand (team view navigation)');
                setCurrentBrand(undefined);
            }
        }
    }
})));

export default useAppView;